import type { FinalDecisionVersionDto } from "@synthesis/shared-services/internal/application/types";
import { Accordion } from "@synthesis/shared-ui";

import { AttachmentFileRow, type AttachmentFileMeta } from "../../../../components/AttachmentFileRow";
import type { ApplicationDetailDocumentFile, ApplicationDetailFinalDecisionRemark } from "../../application-detail-page.lib";
import { DetailFieldRow } from "../detail-field-row";
import { formatFinalDecisionAuthorName, formatFinalDecisionDate, getFinalDecisionDecisionTypeLabel } from "./final-decision.lib";
import { FinalDecisionRemarksSection } from "./final-decision-remarks-section";

const VERSION_STATUS_BADGE_CLASS = "bg-neutrals-95 subtitle-xs text-type-minor inline-flex shrink-0 items-center rounded-lg px-2 py-0.5";
const ACCORDION_CLASS_NAME = "[&>div:last-child]:px-4 [&>div:last-child]:pb-3 [&>div:last-child]:pt-1";

type FinalDecisionVersionAccordionProps = {
  requestId: string;
  version: FinalDecisionVersionDto;
  documents: ApplicationDetailDocumentFile[];
  remarks: ApplicationDetailFinalDecisionRemark[];
  initialOpen: boolean;
  isCurrentVersion?: boolean;
  showDecisionDate?: boolean;
  showAuthor?: boolean;
  canManageRemarks?: boolean;
  onDownload: (fileMeta: AttachmentFileMeta) => void;
};

const toAttachmentFileMeta = (file: ApplicationDetailDocumentFile): AttachmentFileMeta => ({
  id: file.id,
  baseName: file.baseName,
  ext: file.ext,
});

export const FinalDecisionVersionAccordion = ({
  requestId,
  version,
  documents,
  remarks,
  initialOpen,
  isCurrentVersion = false,
  showDecisionDate = true,
  showAuthor = true,
  canManageRemarks = false,
  onDownload,
}: FinalDecisionVersionAccordionProps) => {
  const submittedAt = formatFinalDecisionDate(version.submittedAt);
  const title = showDecisionDate && submittedAt !== "—" ? `Итоговое решение от ${submittedAt}` : "Итоговое решение";

  return (
    <Accordion
      initialOpen={initialOpen}
      value={version.versionNumber != null ? `final-decision-version-${version.versionNumber}` : undefined}
      title={
        <span className="flex min-w-0 items-center gap-2">
          <span className="subtitle-base text-type-common truncate">{title}</span>
          {isCurrentVersion ? <span className={VERSION_STATUS_BADGE_CLASS}>Актуальная версия</span> : null}
        </span>
      }
      className={ACCORDION_CLASS_NAME}
    >
      <DetailFieldRow label="Результат рассмотрения">{getFinalDecisionDecisionTypeLabel(version.decision)}</DetailFieldRow>
      <DetailFieldRow label="Дата принятия решения">{showDecisionDate ? submittedAt : "—"}</DetailFieldRow>
      {showAuthor && formatFinalDecisionAuthorName(version.author) ? (
        <DetailFieldRow label="Автор">{formatFinalDecisionAuthorName(version.author)}</DetailFieldRow>
      ) : null}
      <DetailFieldRow label="Причина решения">{version.justification?.trim() || "—"}</DetailFieldRow>
      <DetailFieldRow label="Документы" className={remarks.length > 0 || canManageRemarks ? "border-b-0" : undefined}>
        {documents.length === 0 ? (
          "—"
        ) : (
          <ul className="flex list-none flex-col gap-1">
            {documents.map((file) => (
              <AttachmentFileRow key={file.id} fileMeta={toAttachmentFileMeta(file)} download={onDownload} />
            ))}
          </ul>
        )}
      </DetailFieldRow>
      <FinalDecisionRemarksSection requestId={requestId} remarks={remarks} canManageRemarks={canManageRemarks} />
    </Accordion>
  );
};
